import { BrowserIdleTracker } from '@metaos/mfs-idle-tracker';
import { ClientConfigProvider, OdspClient } from '@metaos/mfs-loader';
import { MfsWebLocalStore } from '@metaos/mfs-local-store';
import { IS_BROWSER_ENVIRONMENT, MfsStatusCode, } from '@metaos/mfs-telemetry';
import { FoldersClient } from '../../client/foldersClient';
import { mfsErrorHandler, wrapCompletedCallback } from '../../errors/mfsErrorHandler';
import { getCacheKey, updateCache } from '../../utility/cacheUtility';
import { FOLDER_POD_NAME_SUFFIX, PERSONAL_POD } from '../../utility/constant/constants';
import { logger, setLoggerContext } from '../../utility/telemetry';
/** Represents the client builder for prod development environment. */
export class FoldersProdClientBuilder {
    constructor(config) {
        var _a, _b;
        this.config = config;
        this.mfsLocalStore = IS_BROWSER_ENVIRONMENT ? new MfsWebLocalStore() : undefined;
        const configProvider = this.initializeConfigProvider();
        const idleTrackerPromise = this.createIdleTracker(configProvider);
        const odspClient = new OdspClient({
            hostConfig: {},
            getAccessTokenCallback: this.config.mfsAccessTokenCallback,
            getOdspTokenCallback: this.config.odspAccessTokenCallback,
            getPushChannelTokenCallback: this.config.pushChannelTokenCallback,
            sessionId: this.config.sessionId,
            waitForUpdates: (_a = this.config.waitForUpdates) !== null && _a !== void 0 ? _a : true,
            sink: this.config.sink,
            sinkCallback: this.config.sinkCallback,
            disableCache: (_b = this.config.disableCache) !== null && _b !== void 0 ? _b : false,
            setLoggingContext: (loggerContext, sink) => setLoggerContext(loggerContext, sink),
            userCacheKey: config.userCacheKey,
            oneDriveDomain: config.oneDriveDomain,
            localStore: this.mfsLocalStore,
            idleTracker: idleTrackerPromise,
        });
        // Override the `OdspClient.appId` field to use the shared suffix particular to MFS Folders instead of original calling app's ID.
        // This allows Pods created through MFS Folders SDK to be tagged as Folders' Pods and can be accessed by multiple applications when they all use MFS Folders.
        if (config.appId !== FOLDER_POD_NAME_SUFFIX) {
            Reflect.set(odspClient, 'appId', FOLDER_POD_NAME_SUFFIX);
        }
        this.mfsClient = odspClient;
    }
    /** @inheritdoc */
    async build() {
        const { dataStore, containerAttachmentCallback } = await this.getDatastore();
        const client = await FoldersClient.initialize(dataStore, this.mfsClient.clearCache.bind(this.mfsClient), containerAttachmentCallback);
        return client;
    }
    /**
     * Util function that creates or loads a LoadDataStoreResult instance.
     * @returns a `Promise` containing an MfsDataStore
     */
    async getDatastore() {
        try {
            let dataStoreResult;
            const completeCallback = wrapCompletedCallback(this.config.completedCallback);
            if (this.isPodFolderLocator(this.config.podLocation)) {
                const locator = { type: 'odsp', ...this.config.podLocation };
                dataStoreResult = await this.mfsClient.createDataStore(locator, completeCallback);
            }
            else if (this.isPersonalPodLocator(this.config.podLocation)) {
                dataStoreResult =
                    this.config.disableCache || !this.mfsLocalStore
                        ? await this.mfsClient.loadDataStore(completeCallback)
                        : await this.loadDataStoreFromCache(this.mfsLocalStore, completeCallback);
            }
            else {
                const podId = this.config.podLocation;
                dataStoreResult = await this.mfsClient.loadDataStore(completeCallback, podId);
            }
            return dataStoreResult;
        }
        catch (error) {
            throw mfsErrorHandler('Failed to create or load datastore', error);
        }
    }
    /** Util function to check if the pod location identifies an user's personal Pod. */
    isPersonalPodLocator(podLocation) {
        return typeof podLocation === 'string' && podLocation === PERSONAL_POD;
    }
    /** Util function to check if the pod location identifies a folder location in ODSP */
    isPodFolderLocator(podLocation) {
        return typeof podLocation === 'object';
    }
    /**
     * Util method to load data store from the podId stored in local store. If there is no cached Id, then pod is loaded without passing id and localstore is updated with the new value
     * @returns a `Promise` containing an MfsDataStore
     */
    async loadDataStoreFromCache(mfsLocalStore, callback) {
        //Note: This is a temporary assertion as we are guaranteed that userCacheKey is being passed by harmony. Should revisit this to make userCacheKey a mandatory field(TODO)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const userCacheKey = this.config.userCacheKey;
        const cacheKey = getCacheKey(userCacheKey);
        const podId = mfsLocalStore.get(cacheKey);
        let dataStoreResult;
        if (podId) {
            try {
                dataStoreResult = await this.mfsClient.loadDataStore(callback, podId);
                logger.logVerbose('loadDataStoreFromCache', `Retrieved podId from cache and loaded default pod`, {
                    podId,
                });
                return dataStoreResult;
            }
            catch (error) {
                mfsLocalStore.removeEntry(cacheKey);
                logger.logVerbose('loadDataStoreFromCache', `Cached podId invalidated as pod no longer exists`, {
                    podId,
                });
            }
        }
        dataStoreResult = await this.mfsClient.loadDataStore(callback);
        updateCache(this.mfsClient, mfsLocalStore, cacheKey, dataStoreResult.dataStore);
        logger.logVerbose('loadDataStoreFromCache', `Cache empty, updating cache with new podId and loaded default pod`, {
            podId: dataStoreResult.dataStore.getId(),
        });
        return dataStoreResult;
    }
    /** Initializes, starts and returns a ClientConfigProvider instance */
    initializeConfigProvider() {
        ClientConfigProvider.initialize({
            getAccessTokenCallback: this.config.mfsAccessTokenCallback,
            sessionId: this.config.sessionId,
        });
        const configProvider = ClientConfigProvider.getInstance();
        configProvider.start();
        return configProvider;
    }
    /**
     * Creates an idle tracker based on configurations retrieved from a Config Provider. The idle tracker will also subscribe
     * to the Config Provider clientConfigChanged event to change the idle tracker configurations when the Config Provider
     * detects configuration changes.
     */
    async createIdleTracker(configProvider) {
        let idleTracker;
        try {
            const clientConfig = await configProvider.retrieveClientConfig();
            idleTracker = new BrowserIdleTracker({
                isEnabled: clientConfig.containerDisconnects.enableContainerDisconnects,
                idleTimeoutMs: clientConfig.containerDisconnects.allottedTimeBeforeDisconnectMilliseconds,
            });
        }
        catch (unknownError) {
            const error = unknownError instanceof Error ? unknownError : undefined;
            logger.logError('idleTrackerInitializeError', MfsStatusCode.UnprocessableEntity, { error }, 'createIdleTracker');
            // We can still create the tracker in disabled state. That way, we can start the tracker if the configuration
            // provider succeeds in subsequent retrievals.
            idleTracker = new BrowserIdleTracker({
                isEnabled: false,
                idleTimeoutMs: 0,
            });
        }
        configProvider.on('clientConfigChanged', (newClientConfig) => {
            idleTracker.changeConfiguration({
                isEnabled: newClientConfig.containerDisconnects.enableContainerDisconnects,
                idleTimeoutMs: newClientConfig.containerDisconnects.allottedTimeBeforeDisconnectMilliseconds,
            });
        });
        return idleTracker;
    }
}
//# sourceMappingURL=prodClientBuilder.js.map